<template>
	<div class="cart-validation" :class="{ 'cart-validation--split': !confirmation_requested }" v-if="items_in_cart.length">
		<div>
			<div v-if="!confirmation_requested" class="validation-text">{{ __("Your choice") }}</div>
			<div class="small text-muted" v-if="trial">{{ __("Evaluation is free, no payment required") }}</div>
		</div>
		<div>
			<!-- A guest identifies before ordering. GuestOrderIdentification is that step:
			     today a link to log in, later the email-code entry, with nothing around it
			     to change. -->
			<guest-order-identification v-if="is_guest" />
			<div v-else>
				<div class="text-right">
					<!-- The basket kept as a quote (quotes.create_quote); the basket stays as it is. -->
					<button class="btn btn-primary mr-2" @click="make_quote()" v-if="!confirmation_requested" :disabled="quote_requested || contact_changing">{{ __("Make a Quote") }}</button>
					<button class="btn btn-primary mr-2" @click="ask_for_trial_confirmation" v-if="!confirmation_requested">{{ __("For 15 days trial period") }}</button>
					<button class="btn btn-primary" @click="ask_for_confirmation" v-if="!confirmation_requested">{{ __("To order") }}</button>
				</div>
				<div class="form-group checkbox" v-if="confirmation_requested && !stored_vat_number && !trial">
					<label class="form-label" for="vat_number">{{ __("Your company's VAT Number") }}</label>
					<input type="text" id="vat_number" class="form-control" placeholder="BE1234567890" v-model="vat_number" :disabled="vat_state === 'checking'">
					<div class="small text-muted">{{ __("With your VAT number, we directly have your company details") }}</div>
					<div class="small text-muted" v-if="vat_check_supported">{{ __("Your VAT number will be validated in the process") }}</div>
				</div>
				<!-- The customer's own number, not validated yet: shown as it is, the one VIES is asked about. -->
				<div class="form-group" v-if="confirmation_requested && stored_vat_number && !trial && vat_step">
					<label class="form-label">{{ __("Your company's VAT Number") }}</label>
					<div>{{ stored_vat_number }}</div>
					<div class="small text-muted">{{ __("Your VAT number will be validated in the process") }}</div>
				</div>
				<!-- What VIES answered (check_vat_number), as text. -->
				<div class="form-group" v-if="confirmation_requested && vat_step && vat_answered">
					<template v-if="vat_state === 'found'">
						<div>{{ __("Please confirm your company details") }}</div>
						<div class="border rounded p-2 mt-1">
							<div>{{ vat_answer.number }}</div>
							<template v-if="vat_answer.disclosed">
								<div v-if="vat_answer.full_name"><b>{{ vat_answer.full_name }}</b></div>
								<div v-if="vat_answer.address">{{ vat_answer.address }}</div>
								<div v-if="vat_answer.postal_code || vat_answer.city">{{ [vat_answer.postal_code, vat_answer.city].filter(Boolean).join(" ") }}</div>
								<div v-if="vat_answer.country_id">{{ vat_answer.country_id }}</div>
							</template>
							<div class="small text-muted" v-else>{{ __("VIES confirms this VAT number without disclosing the company details") }}</div>
						</div>
					</template>
					<div class="text-danger" v-else-if="vat_state === 'not_found'">{{ __("We cannot find your VAT number") }}</div>
					<div class="text-danger" v-else>{{ __("The European VAT number check service does not seem to be active") }}</div>
					<!-- The answer's own buttons: its details confirmed (FOUND), and another number for one typed here. -->
					<div class="d-flex flex-wrap align-items-center mt-2" v-if="(vat_state === 'found' && !vat_skipped) || !stored_vat_number">
						<button class="btn btn-primary btn-sm mr-2 mb-1" @click="confirm_vat_details()" v-if="vat_state === 'found' && !vat_skipped && !vat_details_confirmed">{{ __("Confirm these details") }}</button>
						<span class="text-success mr-3 mb-1" v-if="vat_state === 'found' && vat_details_confirmed">✓ {{ __("Details confirmed") }}</span>
						<button class="btn btn-default btn-sm mb-1" @click="change_vat_number()" v-if="!stored_vat_number">{{ __("Change the number") }}</button>
					</div>
				</div>
				<div class="form-group checkbox" v-if="confirmation_requested && vat_step && vat_answered && !vat_details_confirmed">
					<label class="form-label" for="vat_skipped">
						<span class="input-area">
							<input type="checkbox" id="vat_skipped" class="form-control" v-model="vat_skipped">
						</span>
						<span class="label-area">{{ __("Continue without a validated VAT number") }}</span>
					</label>
				</div>
				<div class="form-group checkbox"  v-if="confirmation_requested && !trial">
					<label class="form-label" for="accept_terms">
						<span class="input-area">
							<input type="checkbox" id="accept_terms" class="form-control" v-model="accept_terms">
						</span>
						<span class="label-area"><a :href="terms_url" target="_blank">{{ __("I have read the Term of use and sales before this order") }}</a></span>
					</label>
				</div>
				<div class="form-group checkbox"  v-if="confirmation_requested && !trial && var_terms_shown">
					<label class="form-label" for="accept_var_terms">
						<span class="input-area">
							<input type="checkbox" id="accept_var_terms" class="form-control" v-model="accept_var_terms">
						</span>
						<span class="label-area"><a :href="var_terms_url" target="_blank">{{ __("I have read and I accept the VAR Expert Sales condition") }}</a></span>
					</label>
				</div>
				<div class="form-group checkbox" v-if="confirmation_requested && !trial">
					<label class="form-label" for="cart_clearance">
						<span class="input-area">
							<input type="checkbox" id="cart_clearance" class="form-control" v-model="cart_clearance">
						</span>
						<span class="label-area">{{ __("I am a person authorized to commit the company for this type of order") }}</span>
					</label>
				</div>
				<div class="form-group checkbox" v-if="confirmation_requested && trial">
					<label class="form-label" for="trial_terms">
						<span class="input-area">
							<input type="checkbox" id="trial_terms" class="form-control" v-model="trial_terms">
						</span>
						<span class="label-area">{{ __("I have read and by checking this box I accept the terms of use of the ioi software range") }}</span>
					</label>
				</div>
				<div class="text-right">
					<!-- One main button at a time: the order's own, or while the VAT step waits for a number's check, the check. -->
					<button class="btn btn-danger text-right" @click="validate_cart(vat_number, trial)" v-if="!trial && confirmation_requested && cart_clearance && accept_terms && (!var_terms_shown || accept_var_terms) && vat_main === 'order'" :disabled="confirmation_clicked || vat_state === 'checking'">{{ __("Confirm your order") }}</button>
					<button class="btn btn-primary" @click="check_vat_number()" v-if="!trial && confirmation_requested && cart_clearance && accept_terms && (!var_terms_shown || accept_var_terms) && vat_main === 'verify'" :disabled="vat_state === 'checking'">{{ __("Verify my VAT number") }}</button>
					<button class="btn btn-primary" @click="check_vat_number()" v-if="!trial && confirmation_requested && cart_clearance && accept_terms && (!var_terms_shown || accept_var_terms) && vat_main === 'check_again'" :disabled="vat_state === 'checking'">{{ __("Check again") }}</button>
					<button class="btn btn-danger text-right" @click="validate_cart(vat_number, trial)" v-if="trial && confirmation_requested && trial_terms" :disabled="confirmation_clicked">{{ __("Confirm your trial request") }}</button>
				</div>
				<div class="small text-muted text-right" v-if="vat_state === 'checking'">{{ __("Checking your VAT number…") }}</div>
			</div>
		</div>

	</div>
</template>

<script setup>

import { ref, computed, watch, nextTick } from 'vue'
import GuestOrderIdentification from "./GuestOrderIdentification.vue";
import { after_messages, call_quote_write, show_message, when_hidden } from "./quote_actions";
import { server_messages } from "./quote_actions";

// get_subscription: the cart's answer asked for again (CartPage.vue, through SubscriptionColumn.vue), once the
// focus's contact has been switched from "Make a Quote".
const emit = defineEmits(['validate_cart', 'remove_non_eval_items', 'get_subscription'])

const is_guest = ioi_partners.is_guest()

const props = defineProps({
	subscription_object: {
		type: Object,
		default: () => { return {} }
	}
})

const confirmation_requested = ref(false)
const cart_clearance = ref(false)
const accept_terms = ref(false)
const trial_terms = ref(false)
const accept_var_terms = ref(false)
const vat_number = ref("")
const trial = ref(false)
const confirmation_clicked = ref(false)
// The VIES check of the order's VAT number (www/subscription.check_order_vat_number, order_vat.py): "pending"
// until checked, "checking" while the server asks VIES, then its answer, FOUND, NOT_FOUND or UNAVAILABLE;
// "validated" when it says there is nothing to check. Its answer as the page shows it, and "Continue without a
// validated VAT number", ticked.
const vat_state = ref("pending")
const vat_answer = ref({})
const vat_skipped = ref(false)
// "Confirm these details" clicked on a FOUND answer; and the state a running check started from.
const vat_details_confirmed = ref(false)
const vat_state_before = ref("pending")
// An order refused for want of a check (VatCheckRequiredError): checked on the next Confirm, whatever the
// cart's answer said.
const vat_required = ref(false)
// order_vat.py's answers and choices, and the refusals the page reads (exc_type).
const FOUND = "found"
const NOT_FOUND = "not_found"
const UNAVAILABLE = "unavailable"
const CONFIRMED = "confirmed"
const SKIPPED = "skipped"
const STALE_PAGE_ERROR = "FocusChangedError"
const VAT_CHECK_REQUIRED_ERROR = "VatCheckRequiredError"
// A quote is being made (make_quote): its button waits, so that one click makes one quote.
const quote_requested = ref(false)
// The quote's contact is being changed ("Change contact", change_contact), from the click until "Make a Quote" is
// back: its button, the page's included, waits, so that one flow runs at a time.
const contact_changing = ref(false)
// A quote's text, as long as quotes.create_quote takes it (PERSONAL_INFO_LENGTH).
const QUOTE_TEXT_LENGTH = 2000
// The "Make a Quote" email choices in a focus, as quotes.py names them (SEND_MODES): the customer and me
// (chosen first), me alone, nobody.
const SEND_MODE_CUSTOMER_AND_ME = "customer_and_me"
const SEND_MODE_ME = "me"
const SEND_MODE_NONE = "none"
// The sidebar's "My Quotes" entry, which the server draws for a user who may see a quote
// (website_overrides.add_website_item_for_partners).
const MY_QUOTES_ENTRY = '.web-sidebar a[href="/my-quotes"]'


const items_in_cart = computed(() => {
	if (props.subscription_object.subscription_items) {
		return props.subscription_object.subscription_items.filter(f => f.status == "In Basket")
	} else {
		return []
	}
})

const non_eval_items = computed(() => items_in_cart.value.filter(item => !item.eval))

// Strict === true on purpose: an absent or undefined has_real_reseller (e.g. a
// stale payload, or a shape mismatch) must fail toward "real reseller" so the
// checkbox stays visible and the gate stays enforced, never the other way round.
const hasRealReseller = computed(() => props.subscription_object.has_real_reseller === true)

// The cart's answer says whether the customer's number is validated (vat_validated), null where the step does not
// run: then the page is as before the step, the subscription's own copy of the number included.
const vat_check_supported = computed(() => typeof props.subscription_object.vat_validated === "boolean")
const stored_vat_number = computed(() => (
	vat_check_supported.value ? props.subscription_object.customer_vat_number : props.subscription_object.vat_number
) || "")
// A paid order whose number, the customer's own or the one typed, is not validated yet.
const vat_step = computed(() => (
	!trial.value
	&& (vat_required.value || props.subscription_object.vat_validated === false)
	&& vat_state.value !== "validated"
	&& Boolean(stored_vat_number.value || vat_number.value.trim())
))
const vat_answered = computed(() => [FOUND, NOT_FOUND, UNAVAILABLE].includes(vat_state.value))
// The step's decision: the details confirmed (FOUND), or "Continue without a validated VAT number" ticked.
const vat_decided = computed(() => (
	vat_answered.value && (vat_skipped.value || (vat_state.value === FOUND && vat_details_confirmed.value))
))
// The main button: the order's own ("order") without the step or once it is decided; "verify" for a number not
// checked yet, "check_again" after VIES did not find it or did not answer, the one a running check started from;
// none (null) while a FOUND answer waits for its decision, which its own buttons take.
const vat_main = computed(() => {
	if (!vat_step.value || vat_decided.value) {
		return "order"
	}
	const state = vat_state.value === "checking" ? vat_state_before.value : vat_state.value
	if (state === NOT_FOUND || state === UNAVAILABLE) {
		return "check_again"
	}
	return state === FOUND ? null : "verify"
})

// Another number typed: the check was the previous one's.
watch(vat_number, () => reset_vat_check())

const terms_url = computed(() => {
	const lang = ioi_partners.eshop_language_code().toUpperCase()
	return `/assets/ioi_partners/terms/Silicon ioi Software Term of use ${lang}.pdf`
})

// The reseller's terms, as the server resolved them (www/subscription.py, reseller_terms_url): the document the
// checkbox links to, "" for a reseller with none, whose checkbox is then left out of the page and of the gate. An
// answer without the key keeps the checkbox and its static link, as before.
const reseller_terms_resolved = computed(() => typeof props.subscription_object.reseller_terms_url === "string")
const var_terms_shown = computed(() => (
	hasRealReseller.value && (!reseller_terms_resolved.value || Boolean(props.subscription_object.reseller_terms_url))
))

const var_terms_url = computed(() => {
	if (reseller_terms_resolved.value) {
		return props.subscription_object.reseller_terms_url
	}
	const lang = ioi_partners.eshop_language_code().toLowerCase()
	return `/assets/ioi_partners/terms/${frappe.scrub(props.subscription_object.var_id)}_${lang}.pdf`
})

function ask_for_confirmation() {
	confirmation_requested.value = true
}

function ask_for_trial_confirmation() {
	if (non_eval_items.value.length) {
		show_non_eval_modal()
		return
	}
	trial.value = true;
	ask_for_confirmation()
}

function show_non_eval_modal() {
	const offending_items = non_eval_items.value
	const product_list_html = offending_items
		.map(item => `<li>${frappe.utils.escape_html(item.product_name || item.product_id)}</li>`)
		.join('')

	const d = new frappe.ui.Dialog({
		title: __("Products not available for evaluation"),
		primary_action_label: __("Remove concerned products"),
		primary_action: () => {
			emit('remove_non_eval_items', offending_items.map(item => item.name))
			d.hide()
		},
		secondary_action_label: __("Cancel"),
		secondary_action: () => d.hide(),
		onhide: () => d.$wrapper.remove(),
	})

	d.$body.append(`
		<p>${__("Some products are not available for evaluation (mainly caused by cost impact with third party for connection)")}</p>
		<ul style="padding-left: 20px">${product_list_html}</ul>
	`)

	d.show()
}

// "Confirm your order": the order, with the customer's choice on the VAT number's check (vat_check: confirmed, or
// skipped with the box ticked).
function validate_cart(vat_number, trial) {
	// A click while the check runs does nothing: the button is disabled only from the next render on.
	if (vat_state.value === "checking") {
		return
	}
	// Nor does an order while the VAT step waits for its decision, the function called directly included.
	if (vat_main.value !== "order") {
		return
	}
	confirmation_clicked.value = true;
	const vat_check = vat_step.value ? (vat_skipped.value ? SKIPPED : CONFIRMED) : null
	emit('validate_cart', vat_number, trial, { vat_check: vat_check, on_refused: on_order_refused })
}

function reset_vat_check() {
	vat_state.value = "pending"
	vat_answer.value = {}
	vat_skipped.value = false
	vat_details_confirmed.value = false
}

// "Confirm these details": the FOUND answer's company is the customer's; "Confirm your order" sends confirmed.
function confirm_vat_details() {
	if (vat_state.value === FOUND && !vat_skipped.value) {
		vat_details_confirmed.value = true
	}
}

// "Change the number" (a number typed here): the answer forgotten, back to "Verify my VAT number", the cursor in the
// field for another number.
function change_vat_number() {
	reset_vat_check()
	nextTick(() => {
		const field = document.getElementById("vat_number")
		if (field) {
			field.focus()
		}
	})
}

// The VIES check (www/subscription.check_order_vat_number). The customer's own number is the one checked, which
// the server reads itself: only a number typed for a customer that has none is sent. A refusal is said here, as
// text (frappe.call shows none, given statusCode and a detached error_msg), and the step is as it was before.
function check_vat_number() {
	if (vat_state.value === "checking") {
		return
	}
	const before = vat_state.value
	vat_state_before.value = before
	vat_details_confirmed.value = false
	vat_state.value = "checking"
	frappe.call({
		method: "ioi_partners.www.subscription.check_order_vat_number",
		args: {
			vat_number: stored_vat_number.value ? "" : vat_number.value,
			expected_customer: ioi_partners.expected_customer(),
		},
		freeze: true,
		statusCode: {},
		error_msg: $("<div>"),
	}).then((r) => {
		const answer = r.message || {}
		vat_skipped.value = false
		if ([FOUND, NOT_FOUND, UNAVAILABLE].includes(answer.status)) {
			vat_answer.value = answer
			vat_state.value = answer.status
		} else {
			// Nothing to check (validated meanwhile, no number, a site without the step): ordered as before.
			vat_required.value = false
			vat_state.value = "validated"
		}
	}, (xhr) => {
		vat_state.value = before
		report_check_failure(xhr)
	})
}

// A check that failed: a page out of date says the server's message, and loads again once it is closed
// (focus.js); too many checks, our own; anything else, the server's message or ours.
function report_check_failure(xhr) {
	const answer = parse_answer(xhr)
	if (answer.exc_type === STALE_PAGE_ERROR) {
		show_message(server_messages(answer))
	} else if (xhr.status === 429) {
		show_message(__("Too many checks. Please try again in a minute."))
	} else {
		show_message(server_messages(answer) || __("Your VAT number could not be checked. Please try again."))
	}
}

// A refused order (SubscriptionColumn.vue): the server's message is on screen (frappe.call shows it). A page out
// of date loads again (focus.js); an order whose number needs its check is checked on the next Confirm; anything
// else may be tried again, with our message where the server sent none. An answer that is no refusal (a server
// error, 500 or more, or no JSON: a proxy's page, no answer at all) does not say whether the order was saved, which
// happens before its mail: the button stays disabled, and once our message is closed the page loads again, with
// the cart as the server has it.
function on_order_refused(xhr) {
	const answer = parse_answer(xhr)
	if (answer.exc_type === STALE_PAGE_ERROR) {
		return
	}
	if (xhr.status >= 500 || !is_json_answer(xhr)) {
		after_messages(() => show_message(
			__("We could not confirm your order. The page will reload: please check your cart before trying again."),
			() => window.location.reload(),
		))
		return
	}
	if (answer.exc_type === VAT_CHECK_REQUIRED_ERROR) {
		vat_required.value = true
		reset_vat_check()
	}
	confirmation_clicked.value = false
	ioi_partners.report_call_failure(xhr, __("Your order could not be placed. Please try again."))
}

function parse_answer(xhr) {
	try {
		return JSON.parse(xhr.responseText) || {}
	} catch (e) {
		// Not a JSON answer, or none at all.
		return {}
	}
}

// An answer Frappe made: a JSON object.
function is_json_answer(xhr) {
	try {
		const answer = JSON.parse(xhr.responseText)
		return answer !== null && typeof answer === "object"
	} catch (e) {
		return false
	}
}

// "Make a Quote": an optional text to recognise the quote by, then the quote (quotes.create_quote), made
// of the basket, which stays as it is. A field-less Dialog with a plain textarea, as this component's own
// dialog above, so that Frappe's form controls need not be loaded. In a focus, a select chooses whom the
// quote is mailed to: the focused contact the cart names (quote_customer_contact) and me (chosen first), me
// alone, or nobody (quotes.create_quote's send_mode). A custom-select: the website's select.form-control
// has no arrow. The contact is named on a muted line under it, which wraps where an option would be cut.
// A text holding < or >, which the server refuses too, is refused here, before the dialog closes: it stays
// open with the text kept, and says why under it. In a valid focus, "Change contact" hands the dialog over to the
// focused customer's contact choice (change_contact). Made again (reopen: after the choice, or a refused quote),
// it is given back its text, its email choice and the contact it names (customer_contact, its line, and contact,
// its name); a first one reads them from the page.
function make_quote({ text = "", send_mode: chosen_mode = null, customer_contact: carried = null, contact = null } = {}) {
	if (quote_requested.value) {
		return
	}
	const customer_contact = carried || props.subscription_object.quote_customer_contact || null
	// The focused contact the dialog names, sent with the quote: a page shown before the focus moved to another
	// contact is out of date (quotes.create_quote's expected_contact). Absent outside a focus.
	const shown_contact = contact || (props.subscription_object.quote_focus || {}).contact
	const quote_focus = props.subscription_object.quote_focus
	const $text = $("<textarea>")
		.addClass("form-control")
		.attr({ id: "quote-text", rows: 3, maxlength: QUOTE_TEXT_LENGTH })
	$text.val(text)
	const $refusal = $("<p>")
		.addClass("small text-danger mt-2 mb-0 d-none")
		.text(__("The text of a quote cannot contain < or >."))
	const $mode = $("<select>")
		.addClass("custom-select")
		.attr({ id: "quote-send-mode" })
		.append(
			$("<option>").val(SEND_MODE_CUSTOMER_AND_ME).text(__("Send to customer contact and me")),
			$("<option>").val(SEND_MODE_ME).text(__("Send only to me")),
			$("<option>").val(SEND_MODE_NONE).text(__("No email")),
		)
		.val(SEND_MODE_CUSTOMER_AND_ME)
	// Made again: the choice it had.
	if (chosen_mode) {
		$mode.val(chosen_mode)
	}
	let personal_info = null
	// The select's choice, null where it is not offered.
	let send_mode = null
	// What opens once the dialog is hidden instead of a quote: the contact choice it was handed over to.
	let next = null
	// The dialog on screen, from its show until it starts hiding (Bootstrap's hide.bs.modal, as its fade-out begins):
	// its own buttons, × and Esc included. A contact choice ready to open (change_contact's ready) opens only while
	// it is.
	let is_open = false
	const d = new frappe.ui.Dialog({
		title: __("Make a Quote"),
		primary_action_label: __("Make a Quote"),
		primary_action: () => {
			const text = ($text.val() || "").trim()
			if (/[<>]/.test(text)) {
				$refusal.removeClass("d-none")
				$text.trigger("focus")
				return
			}
			$refusal.addClass("d-none")
			personal_info = text
			send_mode = customer_contact ? $mode.val() : null
			quote_requested.value = true
			d.hide()
		},
		secondary_action_label: __("Cancel"),
		secondary_action: () => d.hide(),
	})
	d.$body.append(
		$("<label>").attr("for", "quote-text").text(__("Text")),
		$text,
		$refusal,
		$("<p>").addClass("small text-muted mt-2").text(__("Optional: it tells this quote apart in My Quotes.")),
	)
	if (customer_contact) {
		d.$body.append(
			$("<label>").attr("for", "quote-send-mode").text(__("Email")),
			$mode,
			// "Customer contact" on a line of its own, so that no colon is written here: French spaces it (" : ").
			$("<p>")
				.addClass("small text-muted text-break mt-2 mb-0")
				.append($("<span>").addClass("d-block").text(__("Customer contact")), $("<span>").text(customer_contact)),
		)
	}
	// "Change contact" after the contact's line, in a valid focus alone, built as text.
	if (customer_contact && quote_focus) {
		d.$body.find("p.text-break").append(
			$("<button>")
				.attr("type", "button")
				.addClass("btn btn-link btn-sm p-0 ml-2 align-baseline")
				.text(__("Change contact"))
				.on("click", () => change_contact()),
		)
	}
	d.$wrapper.on("hide.bs.modal", () => {
		is_open = false
	})
	// Sent once the dialog is gone, so that the answer's own dialog never meets its closing; handed over to the
	// contact choice instead, that choice opens, and nothing is sent.
	when_hidden(d, () => {
		if (next) {
			next()
		} else if (personal_info !== null) {
			send_make_quote(personal_info, send_mode, customer_contact, shown_contact)
		}
	})
	d.show()
	is_open = true

	// The focused customer's contacts (focus.js choose_focus_contact), this dialog's text, email choice and contact
	// carried as it shows them when it is handed over, what was typed or chosen while the contacts loaded included.
	// The dialog is handed over only once the choice is ready to open (ready), so that contacts that do not load
	// leave it as it is; closed meanwhile (its own buttons), nothing opens. The choice ended, it is made again
	// (reopen), for the contact chosen when it was switched (switch_contact).
	function change_contact() {
		if (contact_changing.value) {
			return
		}
		contact_changing.value = true
		let carry = null
		let handed_over = false
		ioi_partners.choose_focus_contact({
			customer: { name: ioi_partners.expected_customer(), display_name: quote_focus.customer_name },
			selected: shown_contact,
			ready: (open) => {
				if (!is_open) {
					contact_changing.value = false
					return
				}
				carry = { text: $text.val(), send_mode: $mode.val(), customer_contact, contact: shown_contact }
				handed_over = true
				next = open
				d.hide()
			},
			on_chosen: (chosen) => switch_contact(chosen, carry),
			on_end: () => {
				if (handed_over) {
					reopen(carry)
				} else {
					contact_changing.value = false
				}
			},
		})
	}
}

// The contact chosen for the quote (make_quote's change_contact), switched at once (focus.js switch_focus_contact):
// the page's banner and its cart's answer then name it, and "Make a Quote" is made again for it. The contact it
// named already: nothing to switch. Refused: said once, then "Make a Quote" as it was; a page out of date reloads
// once its message is closed (focus.js), and nothing is made again.
function switch_contact(chosen, carry) {
	if (chosen.name === carry.contact) {
		reopen(carry)
		return
	}
	ioi_partners.switch_focus_contact(chosen.name).then((answer) => {
		$(".eshop-focus-banner__text").text(answer.banner)
		emit("get_subscription")
		reopen({ ...carry, customer_contact: answer.label, contact: answer.contact })
	}, (kind) => {
		if (kind === "refused") {
			reopen(carry)
		}
	})
}

// "Make a Quote" made again with values (make_quote's), once no message is on screen (quote_actions.after_messages),
// so that it never opens under one.
function reopen(values) {
	after_messages(() => {
		contact_changing.value = false
		make_quote(values)
	})
}

function send_make_quote(personal_info, send_mode, customer_contact, shown_contact) {
	call_quote_write("create_quote", {
		personal_info: personal_info,
		expected_customer: ioi_partners.expected_customer(),
		// "" outside a focus, where the server compares no contact.
		expected_contact: shown_contact || "",
		// Only where the choice was offered (a focus); left out elsewhere, where the server mails as before.
		...(send_mode === null ? {} : { send_mode: send_mode }),
	}, {
		// Refused, once its message is closed, "Make a Quote" comes back with what was sent, for another try; not for
		// a page out of date (it reloads), an email choice the server does not know or an empty basket, which another
		// try would only meet again (quote_actions.report_refusal).
		after_message: () => reopen({ text: personal_info, send_mode, customer_contact, contact: shown_contact }),
	}).then((result) => {
		quote_requested.value = false
		const url = `/my-quotes?quote=${encodeURIComponent(result.name)}`
		show_message(made_message(result, send_mode, customer_contact), show_my_quotes_entry, {
			title: __("Make a Quote"),
			body: $("<p>").append($("<a>").attr("href", url).text(__("Open the quote"))),
		})
	}, (kind) => {
		// A page out of date reloads (focus.js); anything else leaves the button to try again.
		if (kind !== "stale") {
			quote_requested.value = false
		}
	})
}

// A made quote's message, as it was mailed (send_mode, email_sent): to the customer and me, to me alone, or
// as before the choice (nobody, or no choice outside a focus).
function made_message(result, send_mode, customer_contact) {
	if (send_mode === SEND_MODE_CUSTOMER_AND_ME) {
		if (result.email_sent) {
			return __("Your quote {0} is made and sent to {1}. Your basket is unchanged.", [result.name, customer_contact])
		}
		return __(
			"Your quote {0} is made, but it could not be sent to {1} right now. You can send it again from the quote page. Your basket is unchanged.",
			[result.name, customer_contact],
		)
	}
	if (send_mode === SEND_MODE_ME) {
		if (result.email_sent) {
			return __("Your quote {0} is made and sent to you. Your basket is unchanged.", [result.name])
		}
		return __("Your quote {0} is made, but it could not be sent to you right now. Your basket is unchanged.", [result.name])
	}
	return __("Your quote {0} is made. Your basket is unchanged.", [result.name])
}

// Once a quote's message is closed: this page was drawn before the quote, so after a first quote its
// sidebar has no "My Quotes" entry yet, and the page is loaded again to draw it. "Open the quote" leaves
// the page instead.
function show_my_quotes_entry() {
	if (!document.querySelector(MY_QUOTES_ENTRY)) {
		window.location.reload()
	}
}
</script>